import PropTypes from "prop-types";
import { cn } from "../../lib/utils";

const Loader = ({
  variant = "spinner",
  size = "md",
  color = "primary",
  className = "",
  pageCentered = false,
}) => {
  // Size configurations
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8",
    xl: "w-12 h-12",
  };

  // Color configurations using design tokens
  const colorClasses = {
    primary: "text-primary-3",
    secondary: "text-secondary-2",
    neutral: "text-neutral-8", // #595959
    white: "text-white",
  };

  // Dot size for dots variant
  const dotSizes = {
    sm: "w-1 h-1",
    md: "w-1.5 h-1.5",
    lg: "w-2 h-2",
    xl: "w-3 h-3",
  };

  // Bar dimensions for bars variant
  const barSizes = {
    sm: "w-0.5 h-3",
    md: "w-1 h-4",
    lg: "w-1 h-6",
    xl: "w-1.5 h-8",
  };

  const baseClasses = `${pageCentered ? 'fixed inset-0 flex' : 'inline-flex'} items-center justify-center ${colorClasses[color]} ${className}`

  // Spinner variant - rotating circle
  if (variant === "spinner") {
    return (
      <div className={baseClasses}>
        <svg
          className={`animate-spin ${sizeClasses[size]}`}
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    );
  }

  // Dots variant - bouncing dots
  if (variant === "dots") {
    return (
      <div className={`${baseClasses} space-x-1`}>
        <div
          className={`${dotSizes[size]} bg-current rounded-full animate-bounce`}
          style={{ animationDelay: "0ms" }}
        />
        <div
          className={`${dotSizes[size]} bg-current rounded-full animate-bounce`}
          style={{ animationDelay: "150ms" }}
        />
        <div
          className={`${dotSizes[size]} bg-current rounded-full animate-bounce`}
          style={{ animationDelay: "300ms" }}
        />
      </div>
    );
  }

  // Bars variant - animated bars
  if (variant === "bars") {
    return (
      <div className={`${baseClasses} space-x-1`}>
        <div
          className={`${barSizes[size]} bg-current animate-pulse`}
          style={{ animationDelay: "0ms" }}
        />
        <div
          className={`${barSizes[size]} bg-current animate-pulse`}
          style={{ animationDelay: "150ms" }}
        />
        <div
          className={`${barSizes[size]} bg-current animate-pulse`}
          style={{ animationDelay: "300ms" }}
        />
        <div
          className={`${barSizes[size]} bg-current animate-pulse`}
          style={{ animationDelay: "450ms" }}
        />
      </div>
    );
  }

  // Pulse variant - pulsing circle
  if (variant === "pulse") {
    return (
      <div className={baseClasses}>
        <div
          className={`${sizeClasses[size]} bg-current rounded-full animate-ping opacity-75`}
        />
      </div>
    );
  }

  // Ring variant - rotating ring
  if (variant === "ring") {
    return (
      <div className={baseClasses}>
        <div
          className={`${sizeClasses[size]} border-2 border-current border-t-transparent rounded-full animate-spin`}
        />
      </div>
    );
  }

  // Default to spinner
  return (
    <div className={baseClasses}>
      <svg
        className={`animate-spin ${sizeClasses[size]}`}
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );
};
Loader.propTypes = {
  variant: PropTypes.oneOf(["spinner", "dots", "bars", "pulse", "ring"]),
  size: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
  color: PropTypes.oneOf(["primary", "secondary", "neutral", "white"]),
  className: PropTypes.string,
  pageCentered: PropTypes.bool,
};

// Loading overlay component for full-screen loading
const LoadingOverlay = ({
  isVisible = false,
  variant = "spinner",
  size = "lg",
  message = "Loading...",
  color = "primary",
  className = "",
}) => {
  if (!isVisible) return null;
  const borderClasses = {
    primary: "border border-primary-3",
    secondary: "border border-secondary-2",
    neutral: "border border-neutral-8", // #595959
    white: "border border-white",
  };

  return (
    <div
      className={`fixed inset-0 bg-[var(--color-blackshades-black40)] backdrop-blur-sm flex items-center justify-center z-50 ${className}`}
    >
      <div className={cn("bg-white rounded-lg p-6 shadow-lg flex flex-col items-center space-y-4", borderClasses[color])}>
        <Loader variant={variant} size={size} color={color} />
        {message && (
          <p className="text-sm font-medium">
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

LoadingOverlay.propTypes = {
  isVisible: PropTypes.bool,
  variant: PropTypes.oneOf(["spinner", "dots", "bars", "pulse", "ring"]),
  size: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
  message: PropTypes.string,
  color: PropTypes.oneOf(["primary", "secondary", "neutral", "white"]),
  className: PropTypes.string,
};

// Loading button component
const LoadingButton = ({
  isLoading = false,
  children,
  variant = "primary",
  size = "md",
  className = "",
  disabled = false,
  ...props
}) => {
  const baseClasses =
    variant === "primary"
      ? "bg-[var(--component-button-primary-filled-bg-default)] hover:bg-[var(--component-button-primary-filled-bg-hovered)] text-[var(--component-button-primary-filled-content-default)]"
      : "bg-[var(--component-button-secondary-filled-bg-default)] hover:bg-[var(--component-button-secondary-filled-bg-hovered)] text-[var(--component-button-secondary-filled-content-default)]";

  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base",
  };

  return (
    <button
      className={`
        inline-flex items-center justify-center space-x-2 rounded-md font-medium
        transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed
        ${baseClasses} ${sizeClasses[size]} ${className}
      `}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <Loader
          variant="spinner"
          size={size === "lg" ? "md" : "sm"}
          color="white"
        />
      )}
      <span className={isLoading ? "opacity-75" : ""}>{children}</span>
    </button>
  );
};
LoadingButton.propTypes = {
  isLoading: PropTypes.bool,
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(["primary", "secondary"]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  className: PropTypes.string,
  disabled: PropTypes.bool,
};

export { Loader, LoadingOverlay, LoadingButton };
